import { Icon } from 'components/Icon';
import { MandateSelection } from 'components/MandateSelection/MandateSelection';
import { Modal } from 'components/Modal';
import { RoleSelector } from 'components/RoleSelector/RoleSelector';
import { ToggleSwitch } from 'components/ToggleSwitch';
import { useCRMMandates, useCurrentUser, usePrincipals } from 'hooks/queries';
import { getCurrentPrincipal } from 'lib/currentPrincipal';
import { getUserTypeByEmail } from 'lib/rolesAndPermissions';
import { isObjectValuesFilled, validateEmailAddress } from 'lib/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseUserType, NewUserDetails, Role } from '../../../utils/types';
import { UserDetails } from '../UserDetails/UserDetails';

type AddUserModalProps = {
  errorToShow?: string;
  initialUserData?: Partial<NewUserDetails>;
  isInputDisabled?: boolean;
  onCancel: () => void;
  onConfirm: (newUser: NewUserDetails) => void;
  selectedPrincipalId: string;
};

const createInitialUser = (
  selectedPrincipalId: string,
  initialUserData?: Partial<NewUserDetails>,
): NewUserDetails => ({
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  role: undefined,
  mandateFilters: {
    [selectedPrincipalId]: {
      hasAllMandates: false,
      portfolio: [],
      country: [],
      marketSegment: [],
      territory: [],
    },
  },
  ...initialUserData,
});

export const AddUserModal: React.FC<AddUserModalProps> = ({
  errorToShow,
  initialUserData,
  isInputDisabled,
  onCancel,
  onConfirm,
  selectedPrincipalId,
}) => {
  const { t } = useTranslation();
  const { user, role } = useCurrentUser();
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const [newUser, setNewUser] = useState<NewUserDetails>(
    createInitialUser(selectedPrincipalId, initialUserData),
  );
  const [isEmailValid, setIsEmailValid] = useState(true);

  const userFilters = newUser?.mandateFilters?.[currentPrincipal.id];

  const { data: mandates } = useCRMMandates({
    principalPri: currentPrincipal.id,
  });

  const { data: filteredMandates } = useCRMMandates({
    principalPri: currentPrincipal.id,
    ...(userFilters && {
      filter: {
        hasAllMandates: userFilters?.hasAllMandates,
        countries: userFilters?.country,
        marketSegments: userFilters?.marketSegment,
        portfolios: userFilters?.portfolio,
        territories: userFilters?.territory,
      },
    }),
  });

  const [errorMessage, setErrorMessage] = useState(errorToShow);

  const userDetailsPlaceholder: BaseUserType = {
    firstName: 'Alfonso',
    lastName: 'Rosser',
    email: 'email@domain.eu',
  };

  useEffect(() => {
    setErrorMessage(errorToShow);
  }, [errorToShow]);

  const onClickConfirm = newUser
    ? () => {
        onConfirm(newUser);
        setErrorMessage(errorToShow);
      }
    : undefined;

  const onChangeForm = (
    inputValue: Record<
      string,
      Record<string, string | string[] | Record<string, string[]>> | string
    >,
  ) => {
    setNewUser({ ...newUser, ...inputValue });
  };

  const validateNewUsersEmailAdress = (value: string) => {
    setErrorMessage(undefined);
    if (!isEmailValid && validateEmailAddress(value)) {
      setIsEmailValid(true);
    }

    if (user?.email?.toLowerCase() === value) {
      return setErrorMessage("You can't use your own email address");
    }
    if (!validateEmailAddress(value)) {
      return setIsEmailValid(false);
    }

    onChangeForm({ email: value });
  };

  const setUserHasAllMandates = useCallback(
    (isSelected: boolean) => {
      const updatedMandateFilters = {
        mandateFilters: {
          [selectedPrincipalId]: {
            hasAllMandates: isSelected,
            portfolio: [],
            country: [],
            marketSegment: [],
            territory: [],
          },
        },
      };

      setNewUser((prevUser) => ({
        ...prevUser,
        ...updatedMandateFilters,
      }));
    },
    [selectedPrincipalId],
  );

  const filteredMandatesCount = filteredMandates?.count.mandates ?? 0;

  const canNewUserRoleHaveMandateFilters =
    newUser.role !== Role.PRINCIPAL_DEVELOPER;

  return (
    <Modal
      isFullscreen={true}
      headingAlign="left"
      heading={t('modals:addUserModal:heading')}
      confirmText={t('modals:addUserModal:confirm')}
      onCancel={onCancel}
      onConfirm={onClickConfirm}
      isConfirmButtonDisabled={
        (newUser && !isObjectValuesFilled(newUser)) || !isEmailValid
      }
    >
      <form>
        <div className="text-grey-700">
          <p className="mb-6 mt-1">{t('modals:addUserModal:description')}</p>
          <UserDetails
            initialUserData={initialUserData}
            isInputDisabled={isInputDisabled}
            placeholder={userDetailsPlaceholder}
            heading={t('modals:addUserModal:personalDetailsHeading')}
            isEmailValid={isEmailValid}
            errorMessage={errorMessage}
            validateNewUsersEmailAdress={validateNewUsersEmailAdress}
            onChangeForm={onChangeForm}
          />
          <fieldset>
            <label className="mb-1 mt-0 block font-medium">
              {t('modals:addUserModal:role')}
            </label>
            {/* TODO: userType is based on newUser.email to define roles to select, could/should be changed? */}
            <RoleSelector
              onChange={(newRole) => {
                onChangeForm({ role: newRole });
                setUserHasAllMandates(
                  !!newUser.mandateFilters[selectedPrincipalId]?.hasAllMandates,
                );
              }}
              userRole={role}
              userType={getUserTypeByEmail(
                isEmailValid ? newUser?.email : undefined,
              )}
            />
          </fieldset>
          {canNewUserRoleHaveMandateFilters && (
            <div className="my-7">
              <div className="mb-3">
                <div className="mb-1 grid grid-flow-row grid-cols-2 ">
                  <h3 className="items-center text-2xl font-medium">
                    {t('modals:addUserModal:mandateSelectionHeading')}
                  </h3>
                  <div className="flex justify-end">
                    <ToggleSwitch
                      label={t('modals:addUserModal:toggleLabel')}
                      onChange={setUserHasAllMandates}
                    />
                  </div>
                </div>

                <div className="mb-2 grid grid-flow-row grid-cols-2">
                  <div className="text-text1 flex items-center justify-start">
                    {t(
                      `modals:addUserModal:numberOfSelectedMandates_${
                        filteredMandatesCount === 1 ? 'one' : 'other'
                      }`,
                      {
                        count: filteredMandatesCount,
                      },
                    )}
                  </div>
                  <div className="text-text2 flex items-center justify-end">
                    <span className="text-2xs">
                      {t('modals:addUserModal:editHasAllMandatesDescription')}
                    </span>
                    <span className="ml-1 w-4">
                      <Icon name="InfoCircle" />
                    </span>
                  </div>
                </div>
              </div>
              <MandateSelection
                mandates={mandates}
                handleMandateFilterChange={(mandateFiltersItems) =>
                  onChangeForm({
                    mandateFilters: {
                      [selectedPrincipalId]: mandateFiltersItems,
                    },
                  })
                }
                initMandateFilters={newUser?.mandateFilters}
                selectedPrincipal={currentPrincipal}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};
